import React from "react";
import { Button, Modal } from "../../../components/ui";
import setupBrandArt from "../../../assets/svg/welcome/setup-brand.png";

export interface BrandWelcomeModalProps {
  /** Takes them to My brand. Also counts as answering the dialog. */
  onSetupBrand: () => void;
  /** Leaves them on the dashboard. Escape and the backdrop land here too. */
  onDismiss: () => void;
}

/**
 * The first thing a workspace with no brand sees on the dashboard.
 *
 * A dialog rather than another line on the page because the setup checklist is
 * already there and is being walked past — this asks once, at the one moment
 * the screen behind it has nothing else on it worth reading, and then gets out
 * of the way. `useBrandWelcome` owns when that moment is and how often it comes
 * round.
 *
 * No heading bar: the pill, the sentence and the artwork are the whole message,
 * and a "Set your brand up" title above a panel that says it better would be
 * the same thing twice. The dialog is still named to a screen reader — see
 * `Modal`'s `hideTitle`.
 *
 * Both ways out are real. Nothing here is required to use the app, so "I'll do
 * this later" is a plain button beside the other rather than a link tucked in a
 * corner.
 *
 * It has to be answered by one of those two, though, which is why the backdrop
 * does not dismiss it: this is a question with two buttons under it, not a
 * panel being shown, and it is only asked once a session — a stray click beside
 * it is far likelier to be a miss than a decision, and taking it as one spends
 * the only showing there was. Escape still closes it, and counts as later.
 */
const BrandWelcomeModal: React.FC<BrandWelcomeModalProps> = ({
  onSetupBrand,
  onDismiss,
}) => (
  <Modal
    open
    hideTitle
    title="Set your brand up"
    onClose={onDismiss}
    dismissOnBackdrop={false}
    // The panel is a tinted card rather than a white one — the design's own
    // gradient, running from the pale teal at the top left to the saturated one
    // at the bottom right.
    //
    // `bg-[linear-gradient(...)]` compiles to a `background-image`, so it paints
    // over `Modal`'s `bg-white` without fighting it. The shadow does collide —
    // `Modal` sets one on the same panel — so this one is marked important to
    // settle it, rather than depending on which of two arbitrary `shadow-[...]`
    // values Tailwind happens to emit last.
    className="max-w-[980px] bg-[linear-gradient(105.28deg,#D1FAFF_25.67%,#67ECF8_99.99%)] !shadow-[0px_5.14px_25.68px_0px_#00000033]"
  >
    {/* Undoes `Modal`'s body padding, so the artwork can run to the panel's
        own edges — the two halves are laid out here instead. The negative
        margins are exactly the padding they cancel, so the row ends up the
        width of the panel and nothing overflows it. */}
    {/* The height is a floor set here rather than whatever the copy happens to
        add up to, which ends the panel just under the artwork and makes the
        card read as cropped to it. A floor and not a fixed height, so copy that
        wraps to another line still lengthens the card rather than spilling. */}
    <div className="-mx-6 -my-5 grid md:min-h-[500px] 3xl:min-h-[540px] md:grid-cols-[minmax(0,1fr)_440px]">
      {/* One padding on all four sides, and the half stretched to the card's
          full height so that padding is what is actually seen: the copy starts
          the same distance from the top and the left, and the actions end the
          same distance from the bottom. Centring the block instead would add
          the leftover height as a margin above and below it, which is what made
          the sides read as tighter than the top. */}
      <div className="flex flex-col justify-between  px-16 3xl:px-20 py-24 3xl:py-28">
        <div>
          {/* White rather than the `brand-soft` fill it used to carry: that
              tint and the gradient behind it are within a few points of each
              other at this corner of the panel, which left the pill invisible.
              White is what separates it from a teal card. */}
          <span className="inline-flex rounded-full bg-[#F1FEFF] px-3 py-1 text-[12px] 3xl:text-[13px] font-medium text-[#007E8C] border-[0.5px] border-[#98E4EC]">
            Welcome to Naurist
          </span>

          <h3 className="mt-3 text-[26px] 3xl:text-[30px] font-semibold leading-[1.25] text-ink">
           
           
              <span className="text-brand">Let's start  <br /> with </span> brand setup
          </h3>

          <p className="mt-4 max-w-[400px] text-[15px] 3xl:text-[16px] leading-6 text-[#007E8C]">
            Your brand shows up on every plan, page, and invoice your clients see. Set it once - change it anytime.
          </p>
        </div>

        {/* `justify-between` above puts the card's spare height here, which is
            what carries the actions down to the bottom edge. The margin is the
            floor under that, for the narrow viewport where the artwork is gone
            and there is no spare height to hand out — without it the buttons
            would sit straight under the last line of copy. */}
        <div className=" flex flex-wrap items-center gap-3">
          <Button size="md" onClick={onSetupBrand}>
            Setup brand
          </Button>
          {/* Ghost rather than secondary: declining is not the second half of a
              confirm pair, and an outline around it would give it the same
              weight as the step being offered.

              The colour is the one exception to `Button`'s rule that callers
              leave a variant's visuals alone: the ghost's brand teal sits close
              enough to this panel's teal gradient to read as disabled on it,
              and this is the only button in the app standing on that gradient —
              not a case worth a variant of its own. Important, because the two
              are the same utility and CSS order would otherwise decide it.

              The hover tint goes for the same reason: the ghost's gray-50 wash
              is meant for a white surface, and on this gradient it reads as a
              smudge rather than as the button lighting up. Same `!` and the
              same reason — two `hover:bg-*` on one element. */}
          <Button
            variant="ghost"
            size="md"
            onClick={onDismiss}
            className="!text-[#00424B] hover:!bg-transparent"
          >
            Skip for now
          </Button>
        </div>
      </div>

      {/* Decorative, hence the empty alt — it shows the screen the button leads
          to, which the sentence beside it has already said. Hidden outright on
          a narrow viewport, where stacking it would push the actions below the
          fold for no gain. */}
      {/* `items-end` rather than `items-center`, and no nudge on the image: the
          artwork is cut to sit in the panel's bottom-right corner. Its canvas
          is 1314x1347 with the drawing running to x=1313 and y=1346 — flush to
          its own right and bottom edges, with the 28px of transparent margin
          all on the left and top, where the drop shadow falls. So dropping it
          into this corner at the column's full width is what puts the browser
          window against both borders, with no offset to maintain.
          Re-measure if the asset is ever recut. */}
      <div className="hidden md:flex items-end overflow-hidden rounded-br-lg">
        <img src={setupBrandArt} alt="" className="block w-full" />
      </div>
    </div>
  </Modal>
);

export default BrandWelcomeModal;
